import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import SectionHeader from './SectionHeader';
import { optionalAuthHeaders } from '../utils/billingApi';
import { signerEmailIsLocked, signingInvitesWereSent } from '../utils/inklessInvites';
import './EmailAttachments.css';
import { emailAttachmentDownloadUrl, getContractApiUrl } from '../utils/apiUrls';
import useViewerSubscription from '../hooks/useViewerSubscription';
import {
  canAttemptInklessRecovery,
  canAttemptInklessSend,
  inklessErrorPreventsSend,
} from '../utils/dealActionVisibility';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

const INKLESS_BASE = (BACKEND_URL.endsWith('/contract')
  ? BACKEND_URL.slice(0, -'/contract'.length)
  : BACKEND_URL.replace(/\/contract\/?$/, '')) + '/inkless';

const withUserEmail = (sourceUrl, userEmail) => {
  if (!sourceUrl || !userEmail || /[?&]user_email=/.test(sourceUrl)) {
    return sourceUrl;
  }
  return `${sourceUrl}${sourceUrl.includes('?') ? '&' : '?'}user_email=${encodeURIComponent(userEmail)}`;
};

/** Fetch a portal download URL with axios (bearer injected) and return a blob object URL. */
const useAuthedBlobUrl = (sourceUrl, userEmail) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!sourceUrl) {
      setBlobUrl(null);
      setLoadError(null);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;

    (async () => {
      try {
        setBlobUrl(null);
        setLoadError(null);
        const response = await axios.get(withUserEmail(sourceUrl, userEmail), { responseType: 'blob' });
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      } catch (err) {
        if (!cancelled) {
          setLoadError('Could not load document');
          setBlobUrl(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [sourceUrl, userEmail]);

  return { blobUrl, loadError };
};

const AuthedPreviewImage = ({ src, alt, userEmail }) => {
  const { blobUrl, loadError } = useAuthedBlobUrl(src, userEmail);
  if (blobUrl) {
    return <img src={blobUrl} alt={alt} />;
  }
  if (src && !loadError) {
    return (
      <div className="ea-thumb-placeholder ea-thumb-loading" aria-label="Loading preview">
        <span className="ea-preview-spinner" />
      </div>
    );
  }
  return <div className="ea-thumb-placeholder">No Preview</div>;
};

const SignerConfirmPanel = ({ signers, deliveryRecipient, onChange, onChangeDelivery }) => (
  <div style={{ padding: '12px 0', maxHeight: 280, overflowY: 'auto' }}>
    {signers.map((s, i) => (
      <div key={`${s.party_role}-${i}`} style={{ marginBottom: 12, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
          {s.entity_name ? `${s.entity_name} (rep)` : s.party_role}
        </div>
        {signerEmailIsLocked(s) ? (
          <div className="ea-signer-email-locked" title="Invite will go to this stored email">
            {s.signer_email || 'Email missing from this transaction'}
          </div>
        ) : (
          <input
            type="email"
            value={s.signer_email || ''}
            placeholder="Add email"
            onChange={(e) => onChange(i, { ...s, signer_email: e.target.value })}
            style={{ width: '100%', padding: 8, marginBottom: 6 }}
          />
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={s.signer_first_name || ''}
            placeholder="First name"
            onChange={(e) => onChange(i, { ...s, signer_first_name: e.target.value })}
            style={{ flex: 1, padding: 8 }}
          />
          <input
            type="text"
            value={s.signer_last_name || ''}
            placeholder="Last name"
            onChange={(e) => onChange(i, { ...s, signer_last_name: e.target.value })}
            style={{ flex: 1, padding: 8 }}
          />
        </div>
      </div>
    ))}
    {deliveryRecipient && (
      <div style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Deliver buyer-signed offer to (seller&apos;s agent)</div>
        <input
          type="email"
          value={deliveryRecipient.email || ''}
          placeholder="Seller's agent email"
          onChange={(e) => onChangeDelivery({ ...deliveryRecipient, email: e.target.value })}
          style={{ width: '100%', padding: 8, marginBottom: 6 }}
        />
        <input
          type="text"
          value={deliveryRecipient.name || ''}
          placeholder="Name"
          onChange={(e) => onChangeDelivery({ ...deliveryRecipient, name: e.target.value })}
          style={{ width: '100%', padding: 8 }}
        />
      </div>
    )}
  </div>
);

const Modal = ({
  isOpen, onClose, title, url, versionText, onPrev, onNext, canPrev, canNext, timestamp, userEmail,
  inklessEligible, inklessSendLabel, inklessSessionStatus, inklessInvitesSent, inklessError, splitId, onSendSuccess, autoOpenSigners, getInklessAuthHeaders,
  canSendInkless = true,
  canRecoverInkless = canSendInkless,
}) => {
  const [showSigners, setShowSigners] = useState(false);
  const [signers, setSigners] = useState([]);
  const [deliveryRecipient, setDeliveryRecipient] = useState(null);
  const [phase, setPhase] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [sendOk, setSendOk] = useState(false);
  const [sendOkMessage, setSendOkMessage] = useState('');
  const [committedSessionId, setCommittedSessionId] = useState(null);
  const { blobUrl: previewBlobUrl, loadError: previewLoadError } = useAuthedBlobUrl(
    isOpen && !showSigners ? url : null,
    userEmail,
  );

  const handleDownload = async (event) => {
    event.preventDefault();
    const downloadUrl = (
      emailAttachmentDownloadUrl(splitId, { userEmail, action: 'download' })
      || (url
        ? `${url}${url.includes('?') ? '&' : '?'}action=download${
          userEmail && !/[?&]user_email=/.test(url) ? `&user_email=${encodeURIComponent(userEmail)}` : ''
        }`
        : null)
    );
    if (!downloadUrl) {
      return;
    }
    try {
      setDownloadError(null);
      const response = await axios.get(downloadUrl, { responseType: 'blob' });
      const blobHref = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = blobHref;
      anchor.download = title || 'document';
      anchor.click();
      URL.revokeObjectURL(blobHref);
    } catch (err) {
      setDownloadError(err.response?.data?.error || 'Download failed');
    }
  };

  const loadSigners = async () => {
    if (
      !splitId
      || !userEmail
      || !canSendInkless
      || inklessErrorPreventsSend(inklessError)
    ) return;
    try {
      const headers = getInklessAuthHeaders ? await getInklessAuthHeaders() : {};
      const res = await axios.get(`${INKLESS_BASE}/splits/${splitId}/signers`, {
        headers,
        params: { user_email: userEmail },
      });
      setSigners(res.data.inkless_signers || []);
      setDeliveryRecipient(res.data.delivery_recipient || null);
      setPhase(res.data.phase || null);
      setSendError(res.data.inkless_error || null);
      setShowSigners(true);
    } catch (err) {
      setSendError(err.response?.data?.error || 'Could not load signers');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setShowSigners(false);
      setSendError(null);
      setDownloadError(null);
      setSendOk(false);
      setSendOkMessage('');
      setCommittedSessionId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setShowSigners(false);
    setSendError(null);
    setDownloadError(null);
    setSendOk(false);
    setSendOkMessage('');
    setCommittedSessionId(null);
    setSigners([]);
    setDeliveryRecipient(null);
    setPhase(null);
  }, [splitId]);

  useEffect(() => {
    if (
      isOpen
      && autoOpenSigners
      && splitId
      && userEmail
      && canSendInkless
      && inklessEligible
      && !inklessErrorPreventsSend(inklessError)
      && !showSigners
    ) {
      loadSigners();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoOpenSigners, splitId, userEmail, inklessEligible, canSendInkless]);

  const submitSend = async () => {
    if (!canSendInkless || inklessErrorPreventsSend(sendError || inklessError)) return;
    setSending(true);
    setSendError(null);
    try {
      const headers = getInklessAuthHeaders ? await getInklessAuthHeaders() : {};
      const path = committedSessionId
        ? `${INKLESS_BASE}/splits/${splitId}/resend`
        : `${INKLESS_BASE}/splits/${splitId}/send`;
      const res = await axios.post(
        path,
        { signers, delivery_recipient: deliveryRecipient, phase, user_email: userEmail },
        { headers, params: { user_email: userEmail } },
      );
      if (res.data && res.data.success === false) {
        if (res.data.session_id) setCommittedSessionId(res.data.session_id);
        setSendError(res.data.error || 'The first-signer invitation was not emailed.');
        return;
      }
      setSendOk(true);
      const invitedCount = Array.isArray(signers)
        ? signers.filter((s) => s && s.signer_email).length
        : 0;
      setSendOkMessage(res.data?.message || `Successfully sent out to ${invitedCount} signing parties`);
      setShowSigners(false);
      if (onSendSuccess) onSendSuccess();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.session_id) setCommittedSessionId(data.session_id);
      setSendError(data.error || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const submitRecover = async () => {
    if (!splitId || !userEmail || !canRecoverInkless) return;
    setSending(true);
    setSendError(null);
    try {
      const headers = getInklessAuthHeaders ? await getInklessAuthHeaders() : {};
      const res = await axios.post(
        `${INKLESS_BASE}/splits/${splitId}/recover`,
        { user_email: userEmail },
        { headers, params: { user_email: userEmail } },
      );
      if (onSendSuccess) {
        onSendSuccess({
          recovered: true,
          signed_split_id: res.data?.signed_split_id,
        });
      }
    } catch (err) {
      setSendError(err.response?.data?.error || 'Could not import the signed document yet');
    } finally {
      setSending(false);
    }
  };

  const submitResend = async () => {
    if (!splitId || !userEmail || !canSendInkless) return;
    setSending(true);
    setSendError(null);
    try {
      const headers = getInklessAuthHeaders ? await getInklessAuthHeaders() : {};
      const res = await axios.post(
        `${INKLESS_BASE}/splits/${splitId}/resend`,
        { user_email: userEmail },
        { headers, params: { user_email: userEmail } },
      );
      if (res.data && res.data.success === false) {
        setSendError('Resend failed');
        return;
      }
      setSendOk(true);
      if (onSendSuccess) onSendSuccess();
    } catch (err) {
      setSendError(err.response?.data?.error || 'Resend failed');
    } finally {
      setSending(false);
    }
  };

  const backdropMouseDownOnBackdrop = useRef(false);
  const handleBackdropMouseDown = (event) => {
    backdropMouseDownOnBackdrop.current = event.target === event.currentTarget;
  };
  const handleBackdropClick = (event) => {
    if (event.target !== event.currentTarget) return;
    if (!backdropMouseDownOnBackdrop.current) return;
    const selected = typeof window !== 'undefined' ? window.getSelection()?.toString() : '';
    if (selected) return;
    onClose();
  };

  if (!isOpen) return null;
  const allSignerEmailsPresent = signers.every((s) => String(s?.signer_email || '').trim());
  const invitesSent = signingInvitesWereSent({
    sendOk,
    inklessInvitesSent,
    inklessSessionStatus,
  });
  const defaultSendLabel = inklessSendLabel || 'Send for Signatures';
  const isRecover = defaultSendLabel === 'Retry Signed Document';
  const isResend = defaultSendLabel === 'Resend for Signatures';
  const sendLabel = defaultSendLabel
  const displayError = sendError || inklessError;
  const sendIsKnownBlocked = inklessErrorPreventsSend(displayError);
  const canUseInklessAction = isRecover ? canRecoverInkless : canSendInkless;
  return (
    <div
      className="ea-modal-backdrop"
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      <div
        className="ea-modal"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={showSigners ? { maxWidth: 520, userSelect: 'text' } : { userSelect: 'text' }}
      >
        <div className="ea-modal-header">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', flex: 1 }}>
            <h3 className="ea-modal-title" style={{ marginRight: 8 }}>{title}</h3>
            {timestamp && (
              <small style={{ color: '#777' }}>
                {new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </small>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {invitesSent && !showSigners && (
              <span className="ea-signing-invites-sent">Signing Invites Sent</span>
            )}
            {canUseInklessAction
              && inklessEligible
              && (isRecover || !sendIsKnownBlocked)
              && splitId
              && !showSigners && (
              <button
                type="button"
                className="ea-send-signatures-btn"
                onClick={isRecover ? submitRecover : isResend ? submitResend : loadSigners}
                disabled={sending}
                title={sendLabel}
              >
                {sending && (isRecover || isResend) ? (isRecover ? 'Retrying…' : 'Resending…') : sendLabel}
              </button>
            )}
            {url && (
              <button
                type="button"
                onClick={handleDownload}
                className="ea-modal-nav"
                title="Download Document"
                style={{ fontSize: '120%', textDecoration: 'none', color: '#444', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                ⤓
              </button>
            )}
            {typeof onPrev === 'function' && (
              <button
                className="ea-modal-nav"
                onClick={onPrev}
                disabled={!canPrev}
                title="Older version"
                style={{ fontSize: '130%' }}
              >
                &#8249;
              </button>
            )}
            {versionText && <div style={{ color: '#444', fontSize: '130%' }}>{versionText}</div>}
            {typeof onNext === 'function' && (
              <button
                className="ea-modal-nav"
                onClick={onNext}
                disabled={!canNext}
                title="Newer version"
                style={{ fontSize: '130%' }}
              >
                &#8250;
              </button>
            )}
            <button className="ea-modal-close" onClick={onClose}>×</button>
          </div>
        </div>
        {showSigners ? (
          <div className="ea-modal-body" style={{ padding: 16 }}>
            <SignerConfirmPanel
              signers={signers}
              deliveryRecipient={deliveryRecipient}
              onChange={(i, val) => setSigners((prev) => prev.map((p, j) => (j === i ? val : p)))}
              onChangeDelivery={setDeliveryRecipient}
            />
            {displayError && <div style={{ color: '#b91c1c', marginBottom: 8 }}>{displayError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowSigners(false)}>Cancel</button>
              <button
                type="button"
                disabled={
                  sending
                  || !canSendInkless
                  || !allSignerEmailsPresent
                  || sendIsKnownBlocked
                }
                onClick={submitSend}
                style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 4 }}
              >
                {sending ? 'Sending…' : 'Confirm & Send'}
              </button>
            </div>
          </div>
        ) : (
          <div className="ea-modal-body">
            {sendOk && sendOkMessage && (
              <div className="ea-signing-send-ok">
                {sendOkMessage}
              </div>
            )}
            {displayError && (
              <div style={{ padding: 8, color: '#b91c1c' }}>{displayError}</div>
            )}
            {previewLoadError && (
              <div style={{ padding: 16, color: '#b91c1c' }}>{previewLoadError}</div>
            )}
            {downloadError && (
              <div style={{ padding: 8, color: '#b91c1c' }}>{downloadError}</div>
            )}
            {!previewLoadError && previewBlobUrl && (
              <iframe title={title} src={previewBlobUrl} className="ea-modal-iframe" />
            )}
            {!previewLoadError && !previewBlobUrl && url && (
              <div className="ea-loading">Loading document…</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EmailAttachments = ({
  contractId,
  listingId,
  buyerAgreementId,
  referralAgreementId,
  propertyAddress,
  isExpanded = false,
  onToggle,
  title = 'Transaction Documents',
  canSendInkless = true,
  canRecoverInkless = true,
}) => {
  const { user, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const { isGuest, loaded: viewerLoaded } = useViewerSubscription();
  const getInklessAuthHeaders = useCallback(
    () => optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims }),
    [getAccessTokenSilently, getIdTokenClaims],
  );
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeModal, setActiveModal] = useState(null); // {title, url, versions, index, splitId, inkless...}
  const [autoOpenSigners, setAutoOpenSigners] = useState(false);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Define params first
  const params = useMemo(() => {
    const p = {};
    if (contractId) p.contract_id = contractId;
    if (listingId) p.listing_id = listingId;
    if (buyerAgreementId) p.buyer_agreement_id = buyerAgreementId;
    if (referralAgreementId) p.referral_agreement_id = referralAgreementId;
    if (propertyAddress && !listingId && !buyerAgreementId && !referralAgreementId) p.property_address = propertyAddress;
    if (debouncedSearch) p.q = debouncedSearch;
    if (user?.email) p.user_email = user.email;
    return p;
  }, [contractId, listingId, buyerAgreementId, referralAgreementId, propertyAddress, debouncedSearch, user?.email]);

  // Define fetchAttachments before using it in useEffect
  const fetchAttachments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use configured BACKEND_URL which now includes /contract from .env.local
      // or ensure we append it if BACKEND_URL is just the base domain
      const baseUrl = BACKEND_URL.endsWith('/contract') ? BACKEND_URL : `${BACKEND_URL}/contract`;
      const response = await axios.get(`${baseUrl}/email-attachments-split`, { params });

      const { attachments } = response.data || { attachments: [] };
      const next = Array.isArray(attachments) ? attachments : [];
      setGroups(next);
      return next;
    } catch (err) {
      console.error('Error fetching email attachments:', err);
      setError('Failed to load attachments');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Use useEffect after fetchAttachments is defined
  useEffect(() => {
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, listingId, buyerAgreementId, referralAgreementId, propertyAddress, debouncedSearch, user?.email]);

  const versionModalFields = (v) => ({
    url: emailAttachmentDownloadUrl(v.split_id || v.id, { userEmail: user?.email }) || v.gcs_uri,
    timestamp: v.email_timestamp,
    splitId: v.split_id || v.id,
    inklessEligible: v.inkless_send_eligible,
    inklessSendLabel: v.inkless_send_label,
    inklessSessionStatus: v.inkless_session_status,
    inklessInvitesSent: v.inkless_invites_sent,
    inklessError: v.inkless_error,
  });

  const versionDisplayTitle = (v) => {
    let vName = (v.split_attachment_file_name || '').split('_ATTACHMENT_').pop();
    return vName
      .replace(/_(?:[a-z0-9_]+)_pages\d+to\d+(?=\.[^.]+$)/i, '')
      .replace(/_(?:[a-z0-9_]+)_pages\d+to\d+$/i, '');
  };

  const openModalForFile = (file, versions, displayVersion, cardTitle, { openSigners = false } = {}) => {
    const targetVersion = displayVersion || versions.find(v => v.display) || versions[0];
    const idx = targetVersion
      ? Math.max(0, versions.findIndex(v => (v.split_id || v.id) === (targetVersion.split_id || targetVersion.id)))
      : 0;
    const v = versions[idx] || targetVersion;
    setAutoOpenSigners(openSigners);
    setActiveModal({
      title: cardTitle,
      versions,
      index: idx,
      total: versions.length || 1,
      ...versionModalFields(v),
    });
  };

  useEffect(() => {
    if (loading || !groups.length) return;
    const params = new URLSearchParams(window.location.search);
    const openDoc = params.get('openDoc');
    const action = params.get('action');
    if (!openDoc) return;

    const targetId = parseInt(openDoc, 10);
    if (Number.isNaN(targetId)) return;

    for (const group of groups) {
      for (const file of group.files || []) {
        const versions = Array.isArray(file.versions) ? file.versions : [];
        const match = versions.find(v => (v.split_id || v.id) === targetId);
        if (match) {
          const cardTitle = file.type_display_copy || group.type_display_copy || 'Document';
          if (!isExpanded && typeof onToggle === 'function') {
            onToggle();
          }
          openModalForFile(file, versions, match, cardTitle, {
            openSigners: action === 'sendForSignatures',
          });
          params.delete('openDoc');
          params.delete('action');
          const qs = params.toString();
          const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`;
          window.history.replaceState({}, '', next);
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, groups]);


  const totalAttachments = groups.reduce((sum, group) => sum + (group.files?.length || 0), 0);

  return (
    <div className="emails-section">
      <SectionHeader
        title={title}
        isExpanded={isExpanded}
        onToggle={onToggle}
      >
        <div className="ea-search">
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="ea-search-input"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </SectionHeader>

      {isExpanded && (
        <div className="emails-content">
          {loading && <div className="ea-loading">Loading attachments...</div>}
          {error && <div className="ea-error">{error}</div>}

          {!loading && !error && (
            <div className="ea-grid">
              {groups.flatMap(group =>
                (group.files || []).map(file => ({ ...file, type_display_copy: group.type_display_copy }))
              ).map(file => {
                const versions = Array.isArray(file.versions) ? file.versions : [];
                const displayVersion = versions.find(v => v.display) || versions[0] || {};
                const totalVersions = versions.length || 1;

                // Use type_display_copy as the title, fallback to split_attachment_file_name parsing if needed
                const cardTitle = file.type_display_copy || 'Document';

                return (
                  <div
                    key={`${file.contract_id}-${file.doc_file_type}-${file.doc_title}`}
                    className="ea-card"
                    onClick={() => {
                      openModalForFile(file, versions, displayVersion, cardTitle);
                    }}
                  >
                    <div className="ea-card-body">
                      <div className="ea-card-title" title={cardTitle}>{cardTitle}</div>

                      <div className="ea-sender-info">
                        <strong>From: </strong>
                        {displayVersion.sender_name
                          ? `${displayVersion.sender_name} <${displayVersion.sender_email || ''}>`
                          : (displayVersion.sender_email || '')}
                      </div>

                      <div className="ea-email-timestamp">
                        {displayVersion.email_timestamp ? new Date(displayVersion.email_timestamp).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        }) : ''}
                      </div>
                    </div>
                    <div className="ea-thumb">
                      {displayVersion.preview_image_gcs_uri ? (
                        <AuthedPreviewImage
                          src={
                            emailAttachmentDownloadUrl(displayVersion.split_id || displayVersion.id, {
                              type: 'preview',
                              userEmail: user?.email,
                            }) || displayVersion.preview_image_gcs_uri
                          }
                          alt={cardTitle}
                          userEmail={user?.email}
                        />
                      ) : (
                        <div className="ea-thumb-placeholder">No Preview</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={!!activeModal}
        onClose={() => { setActiveModal(null); setAutoOpenSigners(false); }}
        title={activeModal?.title}
        url={activeModal?.url}
        userEmail={user?.email}
        timestamp={activeModal?.timestamp}
        splitId={activeModal?.splitId}
        canSendInkless={canAttemptInklessSend({
          viewerLoaded,
          isGuest,
          canSendInkless,
          inklessEligible: activeModal?.inklessEligible,
          inklessError: activeModal?.inklessError,
        })}
        canRecoverInkless={canAttemptInklessRecovery({
          canRecoverInkless,
          inklessEligible: activeModal?.inklessEligible,
        })}
        inklessEligible={activeModal?.inklessEligible}
        inklessSendLabel={activeModal?.inklessSendLabel}
        inklessSessionStatus={activeModal?.inklessSessionStatus}
        inklessInvitesSent={activeModal?.inklessInvitesSent}
        inklessError={activeModal?.inklessError}
        autoOpenSigners={autoOpenSigners}
        getInklessAuthHeaders={getInklessAuthHeaders}
        onSendSuccess={async (result) => {
          const nextGroups = await fetchAttachments();
          const signedSplitId = result?.signed_split_id;
          if (result?.recovered) {
            if (signedSplitId) {
              for (const group of nextGroups) {
                for (const file of group.files || []) {
                  const versions = Array.isArray(file.versions) ? file.versions : [];
                  const match = versions.find((v) => (v.split_id || v.id) === signedSplitId);
                  if (match) {
                    const cardTitle = file.type_display_copy || group.type_display_copy || activeModal?.title;
                    openModalForFile(file, versions, match, cardTitle);
                    return;
                  }
                }
              }
            }
            setActiveModal(null);
            return;
          }
          setActiveModal((current) => (
            current
              ? {
                ...current,
                inklessSessionStatus: current.inklessSessionStatus || 'sent',
                inklessInvitesSent: true,
                inklessSendLabel: 'Resend for Signatures',
              }
              : current
          ));
        }}
        versionText={activeModal ? `version ${(activeModal.total ?? 1) - (activeModal.index ?? 0)} of ${activeModal.total ?? 1}` : ''}
        onPrev={activeModal && activeModal.versions && activeModal.index + 1 < activeModal.total ? () => {
          const nextIdx = (activeModal.index ?? 0) + 1; // older
          const v = activeModal.versions[nextIdx] || activeModal.versions[activeModal.versions.length - 1];
          setActiveModal({
            ...activeModal,
            index: nextIdx,
            title: versionDisplayTitle(v),
            ...versionModalFields(v),
          });
        } : undefined}
        onNext={activeModal && activeModal.versions && (activeModal.index ?? 0) > 0 ? () => {
          const nextIdx = (activeModal.index ?? 0) - 1; // newer
          const v = activeModal.versions[nextIdx] || activeModal.versions[0];
          setActiveModal({
            ...activeModal,
            index: nextIdx,
            title: versionDisplayTitle(v),
            ...versionModalFields(v),
          });
        } : undefined}
        canPrev={!!(activeModal && activeModal.versions && activeModal.index + 1 < activeModal.total)}
        canNext={!!(activeModal && activeModal.versions && (activeModal.index ?? 0) > 0)}
      />
    </div>
  );
};

export default EmailAttachments;
